import { useState, useEffect, useRef, useCallback } from 'react';
import { getUnifiedIapAnalytics } from '../../../services/analytics.service';
import { normalizeAnalyticsError } from '../shared/analyticsViewUtils';

/**
 * Custom hook to fetch, cache, and normalize IAP telemetry data across all Hub tabs.
 *
 * @param {Object} filters - Filter parameters.
 * @param {string} filters.organizationId - Organization identifier.
 * @param {string} filters.siteCollectionId - Site collection identifier.
 * @param {string} filters.fromUtc - ISO-8601 UTC start timestamp (e.g. 2026-08-12T00:00:00Z).
 * @param {string} filters.toUtc - ISO-8601 UTC end timestamp (e.g. 2026-09-12T23:59:59Z).
 * @param {Object} [options]
 * @param {boolean} [options.enabled=true] - Whether queries should run.
 * @returns {Object} Tab data slices, loading state, entitlement status, error, and refetch handler.
 */
export function useIapAnalyticsData(filters = {}, options = {}) {
  const { organizationId, siteCollectionId, fromUtc, toUtc } = filters;
  const enabled = options.enabled !== false;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entitlementDenied, setEntitlementDenied] = useState(false);

  // In-memory cache keyed by "org:site:from:to"
  const cacheRef = useRef(new Map());
  const activeRequestKeyRef = useRef(null);

  const queryKey = (organizationId && siteCollectionId && fromUtc && toUtc)
    ? `${organizationId}:${siteCollectionId}:${fromUtc}:${toUtc}`
    : null;

  const formatTelemetryData = (res) => {
    return {
      overview: {
        usageSummary: res.usageSummary?.data || null,
        activeUsers: res.activeUsers?.data || null,
        sessionMetrics: res.sessionMetrics?.data || null,
        isEntitled: Boolean(res.activeUsers?.isEntitled && res.sessionMetrics?.isEntitled),
        errors: {
          usageSummary: res.usageSummary?.error || null,
          activeUsers: res.activeUsers?.error || null,
          sessionMetrics: res.sessionMetrics?.error || null
        }
      },
      flowAdoption: {
        data: res.featureAdoption?.data || null,
        isEntitled: Boolean(res.featureAdoption?.isEntitled),
        error: res.featureAdoption?.error || null
      },
      moduleUsage: {
        data: res.moduleUsage?.data || null,
        isEntitled: Boolean(res.moduleUsage?.isEntitled),
        error: res.moduleUsage?.error || null
      },
      pipelineHealth: {
        errors: res.telemetryErrors?.data || null,
        usageSummary: res.usageSummary?.data || null,
        isEntitled: Boolean(res.telemetryErrors?.isEntitled),
        error: res.telemetryErrors?.error || null
      },
      raw: res,
      hasAdvancedAnalytics: Boolean(res.hasAdvancedAnalytics)
    };
  };

  const fetchData = useCallback(
    async (forceRefresh = false) => {
      if (!enabled || !queryKey) return;

      // Return cached data if available and not forcing refresh
      if (!forceRefresh && cacheRef.current.has(queryKey)) {
        const cached = cacheRef.current.get(queryKey);
        setData(cached);
        setEntitlementDenied(!cached.hasAdvancedAnalytics);
        setError(null);
        setLoading(false);
        return;
      }

      activeRequestKeyRef.current = queryKey;
      setLoading(true);
      setError(null);

      try {
        const result = await getUnifiedIapAnalytics({
          organizationId,
          siteCollectionId,
          fromUtc,
          toUtc
        });

        // Only update state if this request is still the active one
        if (activeRequestKeyRef.current === queryKey) {
          const formatted = formatTelemetryData(result);
          cacheRef.current.set(queryKey, formatted);
          setData(formatted);
          setEntitlementDenied(!result.hasAdvancedAnalytics);
          setError(null);
        }
      } catch (err) {
        if (activeRequestKeyRef.current === queryKey) {
          const message = normalizeAnalyticsError(err, 'Failed to fetch analytics data');
          setError(message);
        }
      } finally {
        if (activeRequestKeyRef.current === queryKey) {
          setLoading(false);
        }
      }
    },
    [enabled, queryKey, organizationId, siteCollectionId, fromUtc, toUtc]
  );

  useEffect(() => {
    fetchData(false);
  }, [fetchData]);

  const refetch = useCallback(() => {
    return fetchData(true);
  }, [fetchData]);

  return {
    data,
    overview: data?.overview || null,
    flowAdoption: data?.flowAdoption || null,
    moduleUsage: data?.moduleUsage || null,
    pipelineHealth: data?.pipelineHealth || null,
    hasAdvancedAnalytics: data?.hasAdvancedAnalytics ?? true,
    entitlementDenied,
    loading,
    error,
    refetch
  };
}

export default useIapAnalyticsData;
